import React, { useContext, useRef, useState } from 'react'
import VisibilityContext from 'src/provider/state-manager/visibilityProvider'
import { catchErrMsg, erroMessage, loglite, requestMessage } from 'src/utils/utility'
import customerCaller from '../../interceptor/customerInterceptor'

const CustomerApiContext = React.createContext<any>(null)

export interface CustomerListItem {
    profileId: number
    customerId: string | null
    username: string | null
    salutation: string | null
    firstName: string | null
    lastName: string | null
    isOnboardingCompleted: boolean | null
    isAccountLocked: boolean
    primaryAccountNumber: string | null
    joinDate: string
}

export interface CustomersMeta {
    total: number
    pageCount: number
    perPage: number
    currentPageNumber: number
}

export interface AppTransactionLimit {
    profileId: number
    accountNumber: string | null
    oneTimeLimit: number
    dailyLimit: number
}

export interface CustomerMetrics {
    totalCustomers: number
    totalNewCustomers: number
    totalExistingCustomers: number
    newCustomersThisWeek: number
    existingCustomersThisWeek: number
}

export interface BankOneTransactionLimit {
    accountType: string | null
    tier: string | null
    previousTier: string | null
    productName: string | null
    transactionPermission: string | null
    accountStatus: string | null
    maximumBalance: number | null
    maximumDeposit: number | null
    isUnavailable: boolean
}

export interface CustomerTransactionLimit {
    appLimit: AppTransactionLimit | null
    appLimitIsUnavailable: boolean
    bankOneLimit: BankOneTransactionLimit
}

export interface CustomerDocuments {
    customerId: string | null
    signature: string | null
    nationalIdCard: string | null
    utilityDocument: string | null
    passPort: string | null
}

export interface CustomerProfileDetail {
    profileId: number
    customerId: string | null
    username: string | null
    salutation: string | null
    firstName: string | null
    lastName: string | null
    middleName: string | null
    phoneNumber: string | null
    gender: string | null
    emailAddress: string | null
    fullAddress: string | null
    dateOfBirth: string | null
    bvn: string | null
    nin: string | null
    idType: string | null
    primaryAccountNumber: string | null
    onboardingMethod: string | null
    profilePictureUrl: string | null
    onboardingCompleted: boolean | null
    onboardingDate: string | null
    lastLoginDate: string | null
    isInstantPaymentEnabled: boolean
    accountUpgradeStatus: string | null
    fullName: string | null
}

export interface CustomerProfileResponse {
    profile: CustomerProfileDetail
    transactionLimit: CustomerTransactionLimit
    documents: CustomerDocuments | null
}

export interface GetCustomersParams {
    Search?: string
    From?: string
    To?: string
    Status?: string
    OnboardingMethod?: string
    PageLimit?: number
    PageNumber?: number
    FetchAll?: boolean
}

// ── CBP customer profiles ────────────────────────────────────────────────
// GET admin/Customers/CbpProfiles
export enum CbpProfileOnboardingStatus {
    Submitted = 0,
    IndemnityRejected = 1,
    IndemnityAccepted = 2,
    OngoingAmlScreening = 3,
    AmlScreeningPassed = 4,
    AmlScreeningFailed = 5,
    Approved = 6,
}

export interface CbpProfileSummary {
    customerId: string | null
    dateOfBirth: string
    firstName: string | null
    internationalCustomerId: string | null
    lastName: string | null
    nationality: string | null
    phoneNumber: string | null
    primaryAccountNumber: string | null
    status: CbpProfileOnboardingStatus
}

export interface CbpProfilesMeta {
    total: number
    pageCount: number
    perPage: number
    currentPageNumber: number
}

export interface GetCbpCustomersParams {
    CustomerId?: string
    Status?: CbpProfileOnboardingStatus
    PageLimit?: number
    PageNumber?: number
    FetchAll?: boolean
}

export interface UpgradeRequestListItem {
    sn: number
    accountNumber: string | null
    customerID: string | null
    requestedAt: string
    status: string | null
}

export interface UpgradeRequestsMeta {
    total: number
    pageCount: number
    perPage: number
    currentPageNumber: number
}

export interface UpgradeRequestDetail {
    accountNumber: string | null
    customerID: string | null
    firstName: string | null
    lastName: string | null
    gender: string | null
    username: string | null
    phoneNumber: string | null
    emailAddress: string | null
    address: string | null
    nin: string | null
    idType: string | null
    idNumber: string | null
    bvn: string | null
    billType: string | null
    isLocked: boolean
    isOnboardingCompleted: boolean | null
    lastLoginDate: string | null
    requestedAt: string
    upgradedAt: string | null
    status: string | null
    statusMessage: string | null
    signatureUrl: string | null
    passportUrl: string | null
    utilityUrl: string | null
    validIDUrl: string | null
}

export interface GetUpgradeRequestsParams {
    Search?: string
    From?: string
    To?: string
    Status?: string
    OnboardingMethod?: string
    PageLimit?: number
    PageNumber?: number
    FetchAll?: boolean
}

export interface UpgradeDecisionPayload {
    accountNumber: string | null
    requestedAt: string
    reason?: string | null
    isApproved: boolean
}

// ── Roles & permissions ───────────────────────────────────────────────────
// GET admin/Permissions
export interface Permission {
    id: number
    code: string | null
    description: string | null
}

// GET admin/Role/Roles
export interface InstitutionRole {
    id: number
    name: string | null
    description: string | null
    permissions: Permission[]
}

// POST admin/Role/CreateRole
// NOTE: no update/delete endpoint has been documented yet — only create + list.
export interface CreateInstitutionRolePayload {
    name: string | null
    description: string | null
    permissions: number[] | null
}

// POST admin/Role/AssignRole
export interface AssignRolePayload {
    userId: number
    roleId: number
}

// ── Audit logs ────────────────────────────────────────────────────────────
// GET admin/Institutions/AuditLogs — paginated list. Note the list projection
// on the backend only fills id/userId/fullName/role/action/model/performedAt/
// activityType — dataBefore/dataAfter come back null here even though the
// shared AuditLogSummary model has the fields; they're only populated by the
// detail-by-id endpoint below.
export interface AuditLogSummary {
    id: number
    userId: number
    profileId: number | null
    customerFullName: string | null
    fullName: string
    role: string
    action: string
    model: string
    dataBefore: string | null
    dataAfter: string | null
    performedAt: string
    activityType: string | null
}

export interface AuditLogsMeta {
    total: number
    pageCount: number
    perPage: number
    currentPageNumber: number
}

export interface GetAuditLogsParams {
    Search?: string
    Actions?: string[]
    Modules?: string[]
    Roles?: string[]
    From?: string
    To?: string
    ActivityType?: string
    PageLimit?: number
    PageNumber?: number
    FetchAll?: boolean
}

// GET admin/Institutions/AuditLogs/{auditLogId} — NOTE: no curl example was
// given for this one, only the path/query param shapes. Path guessed to
// mirror the sibling Institutions audit-log endpoints; verify against the
// real route before relying on it.
export interface AuditLogDetail {
    action: string | null
    activityType: string | null
    dataAfter: string | null
    dataBefore: string | null
    fullName: string | null
    model: string | null
    performedAt: string
    role: string | null
    userId: number
}

export const CustomerApiProvider = (props: any) => {
    const { loader, notifier } = useContext(VisibilityContext)

    // Detail responses are cached in memory per profileId — opening the same
    // customer twice in one session (e.g. back-and-forth from the list)
    // doesn't need a second round trip. Same pattern as the profile/teammate
    // caches in the configuration provider, just without localStorage since
    // this is per-session lookup data rather than "my institution" data.
    const detailCache = useRef<Map<number, CustomerProfileResponse>>(new Map())

    const [customers, setCustomers] = useState<CustomerListItem[]>([])
    const [customersMeta, setCustomersMeta] = useState<CustomersMeta | null>(null)
    const [cbpCustomers, setCbpCustomers] = useState<CbpProfileSummary[]>([])
    const [cbpCustomersMeta, setCbpCustomersMeta] = useState<CbpProfilesMeta | null>(null)
    const [upgradeRequests, setUpgradeRequests] = useState<UpgradeRequestListItem[]>([])
    const [upgradeRequestsMeta, setUpgradeRequestsMeta] = useState<UpgradeRequestsMeta | null>(null)
    const [permissions, setPermissions] = useState<Permission[]>([])
    const [roles, setRoles] = useState<InstitutionRole[]>([])

    async function request<T = any>(fn: () => Promise<any>, label: string): Promise<T | false | null> {
        try {
            loader(true)
            const response = await fn()
            const { data, status } = response
            loglite({ data }, label)

            if (status === 204 || data === undefined || data === null || data === '') {
                return true as unknown as T
            }

            if (data?.responseCode && data.responseCode != '00') {
                notifier.show(requestMessage(data, data.responseDescription))
                return false
            }

            return (data?.data ?? data) as T
        } catch (err) {
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        } finally {
            loader(false)
        }
    }

    async function getCustomers(params: GetCustomersParams) {
        const result = await request<{ meta: CustomersMeta; items: CustomerListItem[] }>(
            () => customerCaller.get(`admin/Customers`, { params }),
            'get customers API',
        )

        if (result && result.items) {
            setCustomers(result.items)
            setCustomersMeta(result.meta)
            return { items: result.items, meta: result.meta }
        }

        setCustomers([])
        setCustomersMeta(null)
        return { items: [], meta: null }
    }

    async function getCustomerProfile(profileId: number, force = false) {
        if (!force && detailCache.current.has(profileId)) {
            return detailCache.current.get(profileId) as CustomerProfileResponse
        }

        const result = await request<CustomerProfileResponse>(
            () => customerCaller.get(`admin/Customers/${profileId}/CustomerProfile`),
            'get customer profile API',
        )

        if (result) detailCache.current.set(profileId, result)
        return result
    }

    async function getCustomerMetrics() {
        const result = await request<CustomerMetrics>(
            () => customerCaller.get(`admin/Customers/metrics`),
            'get customer metrics API',
        )
        return result || null
    }

    // GET admin/Customers/CbpProfiles — Corporate Banking Platform customers.
    // No free-text search param on this endpoint, only CustomerId, so the
    // toolbar's SearchField is wired to CustomerId in the caller.
    async function getCbpCustomers(params: GetCbpCustomersParams) {
        const result = await request<{ meta: CbpProfilesMeta; items: CbpProfileSummary[] }>(
            () => customerCaller.get(`admin/Customers/CbpProfiles`, { params }),
            'get cbp customers API',
        )

        if (result && result.items) {
            setCbpCustomers(result.items)
            setCbpCustomersMeta(result.meta)
            return { items: result.items, meta: result.meta }
        }

        setCbpCustomers([])
        setCbpCustomersMeta(null)
        return { items: [], meta: null }
    }

    async function getUpgradeRequests(params: GetUpgradeRequestsParams) {
        const result = await request<{ meta: UpgradeRequestsMeta; items: UpgradeRequestListItem[] }>(
            () => customerCaller.get(`admin/Customers/GetUpgradeRequests`, { params }),
            'get upgrade requests API',
        )

        if (result && result.items) {
            setUpgradeRequests(result.items)
            setUpgradeRequestsMeta(result.meta)
            return { items: result.items, meta: result.meta }
        }

        setUpgradeRequests([])
        setUpgradeRequestsMeta(null)
        return { items: [], meta: null }
    }

    async function getUpgradeRequest(accountNumber: string, requestedAt: string) {
        const result = await request<UpgradeRequestDetail>(
            () => customerCaller.get(`admin/Customers/GetUpgradeRequest`, { params: { accountNumber, requestedAt } }),
            'get upgrade request detail API',
        )
        return result || null
    }

    async function updateUpgradeDecision(payload: UpgradeDecisionPayload) {
        const result = await request<boolean>(
            () => customerCaller.patch(`admin/Customers/UpgradeDecision`, payload),
            'upgrade decision API',
        )
        return result
    }

    // GET admin/Permissions — flat permission catalogue, no category/group field.
    async function getPermissions() {
        const result = await request<Permission[]>(
            () => customerCaller.get(`admin/Permissions`),
            'get permissions API',
        )

        if (Array.isArray(result)) {
            setPermissions(result)
            return result
        }

        setPermissions([])
        return []
    }

    // GET admin/Role/Roles
    async function getRoles() {
        const result = await request<InstitutionRole[]>(
            () => customerCaller.get(`admin/Role/Roles`),
            'get roles API',
        )

        if (Array.isArray(result)) {
            setRoles(result)
            return result
        }

        setRoles([])
        return []
    }

    // POST admin/Role/CreateRole — returns no body, just 200 on success.
    async function createRole(payload: CreateInstitutionRolePayload) {
        const result = await request<boolean>(
            () => customerCaller.post(`admin/Role/CreateRole`, payload),
            'create role API',
        )
        return result
    }

    // POST admin/Role/AssignRole — used by the user/team-member picker, not
    // by the roles & permissions list itself.
    async function assignRole(payload: AssignRolePayload) {
        const result = await request<boolean>(
            () => customerCaller.post(`admin/Role/AssignRole`, payload),
            'assign role API',
        )
        return result
    }


    return (
        <CustomerApiContext.Provider
            value={{
                customers,
                customersMeta,
                getCustomers,
                getCustomerProfile,
                getCustomerMetrics,
                cbpCustomers,
                cbpCustomersMeta,
                getCbpCustomers,
                upgradeRequests,
                upgradeRequestsMeta,
                getUpgradeRequests,
                getUpgradeRequest,
                updateUpgradeDecision,
                permissions,
                getPermissions,
                roles,
                getRoles,
                createRole,
                assignRole,
            }}
        >
            {props.children}
        </CustomerApiContext.Provider>
    )
}

export default CustomerApiContext